import { useQuery, useMutation, useQueryClient } from 'react-query';
import { UserHttpService } from 'core/services/user';
import { HttpService } from 'core/services/http';

const useCertifications = (cvId = null) => {
    const queryClient = useQueryClient();

    // Query for fetching certifications - filter by CV if cvId is provided
    const { data, isLoading, error } = useQuery(['certifications', cvId], async () => {
        if (cvId) {
            // Load certificates for specific CV
            const service = new HttpService();
            const response = await service.get(`/job/cv/certificate/?cv=${cvId}`, null);
            return Array.isArray(response) ? response : (response?.results || []);
        } else {
            // Load all user certifications
            const response = await UserHttpService.listCertifications();
            return response.results || [];
        }
    });

    // Mutation for adding a certification
    const addCertificationMutation = useMutation(
        async (newCertification) => {
            return await UserHttpService.createCertification(newCertification);
        },
        {
            onSuccess: () => {
                // Invalidate and refetch certifications query
                queryClient.invalidateQueries(['certifications', cvId]);
            }
        }
    );

    // Mutation for updating a certification
    const updateCertificationMutation = useMutation(
        async (updatedCertification) => {
            return await UserHttpService.updateCertification(updatedCertification.id, updatedCertification);
        },
        {
            onSuccess: () => {
                // Invalidate and refetch certifications query
                queryClient.invalidateQueries(['certifications', cvId]);
            }
        }
    );

    // Mutation for deleting a certification
    const deleteCertificationMutation = useMutation(
        async (id) => {
            return await UserHttpService.deleteCertification(id);
        },
        {
            onSuccess: () => {
                // Invalidate and refetch certifications query
                queryClient.invalidateQueries(['certifications', cvId]);
            }
        }
    );

    return {
        certifications: data || [],
        isLoading,
        error: error ? error.message : null,
        addCertification: addCertificationMutation.mutateAsync,
        updateCertification: updateCertificationMutation.mutateAsync,
        deleteCertification: deleteCertificationMutation.mutateAsync
    };
};

export default useCertifications; 